import { useQuery } from "@tanstack/react-query";

import {
  getPatientZoomMeeting,
  getPatientZoomMeetingBillingNote,
  getPatientZoomMeetingRecording,
  getPatientZoomMeetingSoapNote,
  getPatientZoomMeetingSummary,
  getPatientZoomMeetingTranscript,
} from "~/providers/api/patientZoomMeetingsService";
import { ZOOM_RECORDING_STATUS } from "~/providers/constants/zoomRecordingStatus";
import { ZOOM_SUMMARY_STATUS } from "~/providers/constants/zoomSummaryStatus";
import { ZOOM_TRANSCRIPT_STATUS } from "~/providers/constants/zoomTranscriptStatus";
import { isBillingNoteGenerationPending } from "~/providers/utils/sessionBillingNoteStatus";
import { isSoapNoteGenerationPending } from "~/providers/utils/sessionSoapNoteStatus";

interface UseGetPatientZoomMeetingParams {
  patientId: string;
  sessionId: string;
  enabled?: boolean;
}

export const useGetPatientZoomMeeting = ({
  patientId,
  sessionId,
  enabled = true,
}: UseGetPatientZoomMeetingParams) => {
  return useQuery({
    queryFn: () => getPatientZoomMeeting({ patientId, sessionId }),
    queryKey: ["getPatientZoomMeeting", patientId, sessionId],
    enabled: !!patientId && !!sessionId && enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      const isProcessing =
        data?.transcript_status === ZOOM_TRANSCRIPT_STATUS.in_progress ||
        data?.recording_status === ZOOM_RECORDING_STATUS.in_progress ||
        data?.summary_status === ZOOM_SUMMARY_STATUS.in_progress;
      return isProcessing ? 30000 : false;
    },
  });
};

interface UseGetPatientZoomMeetingTranscriptParams extends UseGetPatientZoomMeetingParams {
  transcriptStatus?: string | null;
}

export const useGetPatientZoomMeetingTranscript = ({
  patientId,
  sessionId,
  transcriptStatus,
  enabled = true,
}: UseGetPatientZoomMeetingTranscriptParams) => {
  const isCompleted = transcriptStatus === ZOOM_TRANSCRIPT_STATUS.completed;

  return useQuery({
    queryFn: () => getPatientZoomMeetingTranscript({ patientId, sessionId }),
    queryKey: ["getPatientZoomMeetingTranscript", patientId, sessionId],
    enabled: !!patientId && !!sessionId && enabled && isCompleted,
  });
};

interface UseGetPatientZoomMeetingRecordingParams extends UseGetPatientZoomMeetingParams {
  recordingStatus?: string | null;
}

export const useGetPatientZoomMeetingRecording = ({
  patientId,
  sessionId,
  recordingStatus,
  enabled = true,
}: UseGetPatientZoomMeetingRecordingParams) => {
  const isCompleted = recordingStatus === ZOOM_RECORDING_STATUS.completed;

  return useQuery({
    queryFn: () => getPatientZoomMeetingRecording({ patientId, sessionId }),
    queryKey: ["getPatientZoomMeetingRecording", patientId, sessionId],
    enabled: !!patientId && !!sessionId && enabled && isCompleted,
  });
};

interface UseGetPatientZoomMeetingSummaryParams extends UseGetPatientZoomMeetingParams {
  summaryStatus?: string | null;
}

export const useGetPatientZoomMeetingSummary = ({
  patientId,
  sessionId,
  summaryStatus,
  enabled = true,
}: UseGetPatientZoomMeetingSummaryParams) => {
  const isCompleted = summaryStatus === ZOOM_SUMMARY_STATUS.completed;

  return useQuery({
    queryFn: () => getPatientZoomMeetingSummary({ patientId, sessionId }),
    queryKey: ["getPatientZoomMeetingSummary", patientId, sessionId],
    enabled: !!patientId && !!sessionId && enabled && isCompleted,
  });
};

export const useGetPatientZoomMeetingSoapNote = ({
  patientId,
  sessionId,
  enabled = true,
}: UseGetPatientZoomMeetingParams) => {
  return useQuery({
    queryFn: () => getPatientZoomMeetingSoapNote({ patientId, sessionId }),
    queryKey: ["getPatientZoomMeetingSoapNote", patientId, sessionId],
    enabled: !!patientId && !!sessionId && enabled,
    retry: false,
    refetchInterval: (query) => {
      const soapNote = query.state.data;
      return soapNote && isSoapNoteGenerationPending(soapNote) ? 5_000 : false;
    },
  });
};

export const useGetPatientZoomMeetingBillingNote = ({
  patientId,
  sessionId,
  enabled = true,
}: UseGetPatientZoomMeetingParams) => {
  return useQuery({
    queryFn: () => getPatientZoomMeetingBillingNote({ patientId, sessionId }),
    queryKey: ["getPatientZoomMeetingBillingNote", patientId, sessionId],
    enabled: !!patientId && !!sessionId && enabled,
    retry: false,
    refetchInterval: (query) => {
      const billingNote = query.state.data;
      return billingNote && isBillingNoteGenerationPending(billingNote)
        ? 5_000
        : false;
    },
  });
};
